<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'Job',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    oid: {
      type: String,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const resource = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));
  const b_5962862420439144_style = computed(() => ({
    minHeight: '200px',
  }));

  const $data = computed(() => ({
    resource: resource.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_5962862420439144_style: b_5962862420439144_style.value,
  }));

  const $$load_resource = () => {
    return $fn.value.fetch('read_a_job', {
      parameters: { id: props.oid },
      asResource: true,
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'resource' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_5962862420439144_style', nodeId: '5962862420439144' },
    ],
    loaders: { resource: $$load_resource },
    ux: {},
    dataRefs: {
      resource,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_5962862420439144_style,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: [
          $fn.value.pgettext('Job|Title', 'Job'),
          resource.value.name,
          resource.value.id,
        ],
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.oid,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('oid');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_x4cxljlx9_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_6466705138206980_reloadGrid_2o0we1zhz_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'jobs',
    };
  };

  const act_7315092382398562_closeModal_x4cxljlx9 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_x4cxljlx9_getActionArgs,
    });
  const inst_evh_7315092382398562_close_9iwiefr36 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_x4cxljlx9,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_9iwiefr36],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_27innwbqz =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_rbc799enf =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_27innwbqz,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_rbc799enf],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_lfbzirovz =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_mt2q2rodj =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_lfbzirovz,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_mt2q2rodj],
    event: 'close',
  });
  const act_6466705138206980_reloadComponentData_8cv3p5d6v =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_6466705138206980_reload_7pg2k7acm =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_reloadComponentData_8cv3p5d6v,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_6466705138206980_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6466705138206980_reload_7pg2k7acm],
    event: 'reload',
  });
  const act_6466705138206980_closeModal_fyofg7k8x =
    new actionTypes.CloseModalAction({});
  const inst_evh_6466705138206980_deleted_f40f2sk8w =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_closeModal_fyofg7k8x,
      event: 'deleted',
      displayName: 'closeModal',
    });
  const act_6466705138206980_reloadGrid_2o0we1zhz =
    new actionTypes.ReloadGridAction({
      actionArgs: act_6466705138206980_reloadGrid_2o0we1zhz_getActionArgs,
    });
  const inst_evh_6466705138206980_deleted_93o114niw =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_reloadGrid_2o0we1zhz,
      event: 'deleted',
      displayName: 'reloadGrid',
    });
  const inst_evh_6466705138206980_deleted = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_6466705138206980_deleted_f40f2sk8w,
      inst_evh_6466705138206980_deleted_93o114niw,
    ],
    event: 'deleted',
  });
  const act_1040167445267876_reloadComponentData_syfn7um5g =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_1040167445267876_reload_ka4rluxwy =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_reloadComponentData_syfn7um5g,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_1040167445267876_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_reload_ka4rluxwy],
    event: 'reload',
  });
  const act_2140198529698299_closeComponent_8ag8n4s35 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2140198529698299_clickPreventStop_13o2nl5r5 =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_closeComponent_8ag8n4s35,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2140198529698299_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2140198529698299_clickPreventStop_13o2nl5r5],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_9iwiefr36,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_rbc799enf,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_mt2q2rodj,
    inst_evh_8564662037462133_close,
    inst_evh_6466705138206980_reload_7pg2k7acm,
    inst_evh_6466705138206980_reload,
    inst_evh_6466705138206980_deleted_f40f2sk8w,
    inst_evh_6466705138206980_deleted_93o114niw,
    inst_evh_6466705138206980_deleted,
    inst_evh_1040167445267876_reload_ka4rluxwy,
    inst_evh_1040167445267876_reload,
    inst_evh_2140198529698299_clickPreventStop_13o2nl5r5,
    inst_evh_2140198529698299_clickPreventStop,
  };

  const evh_7315092382398562_close_9iwiefr36 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_9iwiefr36.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_rbc799enf = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_rbc799enf.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_mt2q2rodj = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_mt2q2rodj.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_reload_7pg2k7acm = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_reload_7pg2k7acm.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_reload = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted_f40f2sk8w = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted_f40f2sk8w.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted_93o114niw = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted_93o114niw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_reload_ka4rluxwy = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_reload_ka4rluxwy.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_reload = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop_13o2nl5r5 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop_13o2nl5r5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="Job"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
        <JobHeader
          :item="resource"
          @reload="evh_6466705138206980_reload($event, {})"
          @deleted="evh_6466705138206980_deleted($event, {})"
        >
        </JobHeader>
      </UcHeader>
    </template>
    <div
      class="px-4 py-3 h-100 overflow-hidden"
      :style="b_5962862420439144_style"
    >
      <JobView
        :item="resource"
        v-if="resource"
        @reload="evh_1040167445267876_reload($event, {})"
      >
      </JobView>
    </div>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_2140198529698299_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
